import { defineStore } from 'pinia';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import ApiService from "@/core/services/ApiService";

const DEFAULT_TTL = 1000 * 60 * 10; // 10 menit

// master data yang dipakai dropdown ter-cache; write ke endpoint ini mengosongkan cache
const CACHED_MASTER_DATA_ENDPOINTS = /^\/(property\/agent|property\/room-type|master\/agent-template|master\/payment-collect-type|general\/country)(\/|$)/;
const READ_ONLY_ENDPOINTS = /\/(details?|datatables|dropdown)(\/|$)|-preview$/;

// tipe untuk parameter request
export interface ApiRequestOptions {
    url: string
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'UPDATE'
    params?: Record<string, any>
    slug?: string
    config?: AxiosRequestConfig | undefined
    force?: boolean
    ttl?: number // time-to-live dalam ms
}

// tipe untuk cache entry
interface CacheEntry<T = any> {
    data: T
    _saveAt: number
}

const readEndpoint = (body: unknown): string => {
    if (body instanceof FormData) return String(body.get('endpoint') ?? '');
    if (typeof body !== 'string') return '';
    try {
        return String(JSON.parse(body)?.endpoint ?? '');
    } catch (e) {
        return '';
    }
};

export const isCachedMasterDataWrite = (config: AxiosRequestConfig): boolean => {
    const endpoint = readEndpoint(config.data);
    return CACHED_MASTER_DATA_ENDPOINTS.test(endpoint) && !READ_ONLY_ENDPOINTS.test(endpoint);
};

export const useApiStore = defineStore('api', {
    state: () => ({
        cache: {} as Record<string, CacheEntry>,
    }),
    actions: {
        async setCache<T = any>(options: ApiRequestOptions): Promise<T> {
            const {
                url,
                method = 'POST',
                params = {},
                slug = '',
                config = {},
                force = false,
                ttl = DEFAULT_TTL
            } = options;

            const cacheKey = `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(config)}`;
            const cached = this.cache[cacheKey];

            if (!force && cached && Date.now() - cached._saveAt < ttl) {
                return cached.data as T;
            }

            try {
                let res: AxiosResponse<T>;
                switch (method) {
                    case 'POST':
                        res = await ApiService.post(url, params, config);
                        break;
                    case 'GET':
                        res = await ApiService.get(url, config);
                        break;
                    case 'PUT':
                        res = await ApiService.put(url, params, config);
                        break;
                    case 'UPDATE':
                        res = await ApiService.update(url, slug, params, config);
                        break;
                    case 'DELETE':
                        res = await ApiService.delete(url);
                        break;
                    default:
                        res = await ApiService.post(url, params, config);
                        break;
                }

                this.cache[cacheKey] = {
                    data: res.data,
                    _saveAt: Date.now()
                };

                return res.data;
            } catch (error: any) {
                // console.error('API error:', error);
                throw new Error(error.response);
            }
        },
        getCache(optionsKey: ApiRequestOptions): any | null {
            const {
                url,
                method = 'POST',
                params = {},
                slug = '',
                config = {},
                force = false,
                ttl = DEFAULT_TTL
            } = optionsKey;

            const cacheKey = `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(config)}`;
            const entry = this.cache[cacheKey];
            if (!entry) return null;

            if (Date.now() - entry._saveAt > ttl) {
                // kadaluarsa → hapus
                delete this.cache[cacheKey];
                return null;
            }
            return entry.data;
        },
        clearCache(optionsKey?: ApiRequestOptions) {
            if (!optionsKey) {
                this.cache = {};
                return;
            }

            const {
                url,
                method = 'POST',
                params = {},
                config = {},
            } = optionsKey;

            const cacheKey = `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(config)}`;
            delete this.cache[cacheKey];
        },

    },
    persist: {
        key: 'api-cache',
        storage: sessionStorage,
        serializer: {
            serialize: (state) => JSON.stringify(state),
            deserialize: (value) => {
                try {
                    const obj = JSON.parse(value) as Record<string, CacheEntry>;
                    const newCache: Record<string, CacheEntry> = {};

                    // cek semua key
                    for (const [key, entry] of Object.entries(obj.cache || {})) {
                        if (entry._saveAt && Date.now() - entry._saveAt <= DEFAULT_TTL) {
                            newCache[key] = entry;
                        }
                    }

                    return { cache: newCache };
                } catch (e) {
                    return { cache: {} };
                }
            },
        },
    }
});